import { useCallback } from 'react';
import { useAnalytics } from 'use-analytics';

import { roundToNearest } from '@common/helpers';
import {
    ApplianceModel,
    ColorModel,
    CornerRadiusModel,
    CutoutModel,
    DiagonalCornerModel,
    DrawingShape,
    EdgeModel,
    MaterialModel,
    OptionalProduct,
    PredefinedShape,
    PricedProduct,
    StoreModel,
    TearOutModel,
} from '@common/models';
import { GaItem } from '@common/models/analytics.model';
import { CustomSideModel } from '@common/models/predefined-custom-side.model';
import { getProductDescription } from '@common/utils';

import { PriceTotalsReturn } from '../../pages/easy-estimator/components/price/price.utils';

interface StorePayloadData {
    store: StoreModel;
}

interface MaterialPayloadData {
    material: MaterialModel;
    color: ColorModel | undefined;
}

interface ColorPayloadData {
    color: ColorModel;
}

interface ZipCodePayloadData {
    zipCode: string;
}

interface AddShapePayloadData {
    shape: Partial<PredefinedShape | DrawingShape>;
}

interface DeleteShapePayloadData {
    shape: DrawingShape;
}

interface SelectEdgePayloadData {
    edge: EdgeModel;
}

interface SelectRadiusPayloadData {
    radius: CornerRadiusModel;
}

interface SelectDiagonalPayloadData {
    diagonal: DiagonalCornerModel;
}

interface SelectCustomSidePayloadData {
    side: CustomSideModel;
}

interface SelectApplianceButtonPayloadData {
    appliance: ApplianceModel;
}

interface AddAppliancePayloadData {
    appliance: ApplianceModel;
}

interface DeleteAppliancePayloadData {
    appliance: ApplianceModel;
}

interface SelectCutoutPayloadData {
    cutout: CutoutModel;
}

interface SelectTearoutPayloadData {
    tearout: TearOutModel;
}

interface SelectOptionalItemPayloadData {
    optionalItem: OptionalProduct;
}

interface ReviewEstimatePayloadData {
    pricedProducts: PricedProduct[];
    priceTotals: PriceTotalsReturn;
}

interface BeginCheckoutPayloadData {
    pricedProducts: PricedProduct[];
    priceTotals: PriceTotalsReturn;
}

interface CustomerInfoSavePayloadData {
    pricedProducts: PricedProduct[];
    priceTotals: PriceTotalsReturn;
}

interface EstimateSavedPayloadData {
    estimateNumber: string;
    pricedProducts: PricedProduct[];
    priceTotals: PriceTotalsReturn;
}

interface RoomvoPayload {
    label?: string;
    value?: number;
}

interface VisualizerPayloadData {
    action: string;
    payload: RoomvoPayload;
}

interface ShoppingCart {
    currency: string;
    value: number;
    items: GaItem[];
}

interface SavedShoppingCart extends ShoppingCart {
    transaction_id: string;
}

interface CloneEstimatePayload {
    status: string;
}

interface AnalyticsEventsReturn {
    track: (eventName: string, payload?: any) => void;
    page: (data?: any) => void;
    reset: () => void;
    identify: (userId: string, traits?: any) => void;
    trackSearchZipCode: ({ zipCode }: ZipCodePayloadData) => void;
    trackSelectStore: ({ store }: StorePayloadData) => void;
    trackSelectMaterial: ({ material, color }: MaterialPayloadData) => void;
    trackSelectColor: ({ color }: ColorPayloadData) => void;
    trackAddShape: ({ shape }: AddShapePayloadData) => void;
    trackDeleteShape: ({ shape }: DeleteShapePayloadData) => void;
    trackSelectEdge: ({ edge }: SelectEdgePayloadData) => void;
    trackSelectRadius: ({ radius }: SelectRadiusPayloadData) => void;
    trackSelectDiagonal: ({ diagonal }: SelectDiagonalPayloadData) => void;
    trackSelectCustomSide: ({ side }: SelectCustomSidePayloadData) => void;
    trackSelectApplianceButton: ({ appliance }: SelectApplianceButtonPayloadData) => void;
    trackAddAppliance: ({ appliance }: AddAppliancePayloadData) => void;
    trackDeleteAppliance: ({ appliance }: DeleteAppliancePayloadData) => void;
    trackSelectCutout: ({ cutout }: SelectCutoutPayloadData) => void;
    trackSelectTearout: ({ tearout }: SelectTearoutPayloadData) => void;
    trackSelectOptionalItem: ({ optionalItem }: SelectOptionalItemPayloadData) => void;
    trackViewCart: ({ pricedProducts, priceTotals }: ReviewEstimatePayloadData) => void;
    trackBeginCheckout: ({ pricedProducts, priceTotals }: BeginCheckoutPayloadData) => void;
    trackCustomerInfoSave: ({ pricedProducts, priceTotals }: CustomerInfoSavePayloadData) => void;
    trackEstimateSaved: ({ estimateNumber, pricedProducts, priceTotals }: EstimateSavedPayloadData) => void;
    trackVisualizer: ({ action, payload }: VisualizerPayloadData) => void;
    trackCloneEstimate: ({ status }: CloneEstimatePayload) => void;
}

export const useAnalyticsEvents = (): AnalyticsEventsReturn => {
    const { track, page, reset, identify } = useAnalytics();

    const trackSearchZipCode = ({ zipCode }: ZipCodePayloadData) => {
        track('search', {
            search_term: `zipCode: ${zipCode}`,
        });
    };

    const trackSelectStore = ({ store }: StorePayloadData) => {
        track('select_item', {
            category: 'store',
            items: [{ item_id: store.companyId.toString(), item_name: store.companyName } as GaItem],
        });
    };

    const trackSelectMaterial = ({ material, color }: { material: MaterialModel; color: ColorModel | undefined }) => {
        track('select_item', {
            category: 'material',
            items: [
                {
                    item_id: color?.qteGrpID?.toString() ?? material.description,
                    item_name: material.description,
                } as GaItem,
            ],
        });
    };

    const trackSelectColor = useCallback(
        ({ color }: ColorPayloadData) => {
            track('select_item', {
                category: 'color',
                items: [
                    {
                        item_id: color.productId.toString(),
                        item_name: getProductDescription(color),
                        price: color.price,
                    } as GaItem,
                ],
            });
        },
        [track]
    );

    const trackAddShape = useCallback(
        ({ shape }: AddShapePayloadData) => {
            track('add_shape', {
                shape: shape.type ?? 'Unknown',
            });
        },
        [track]
    );

    const trackDeleteShape = useCallback(
        ({ shape }: DeleteShapePayloadData) => {
            track('remove_shape', {
                shape: shape.type,
            });
        },
        [track]
    );

    const trackSelectEdge = useCallback(
        ({ edge }: SelectEdgePayloadData) => {
            track('select_item', {
                category: 'edge',
                items: [{ item_id: edge.productId.toString(), item_name: getProductDescription(edge) } as GaItem],
            });
        },
        [track]
    );

    const trackSelectRadius = useCallback(
        ({ radius }: SelectRadiusPayloadData) => {
            track('select_item', {
                category: 'radius',
                items: [{ item_id: radius.productId.toString(), item_name: getProductDescription(radius) } as GaItem],
            });
        },
        [track]
    );

    const trackSelectDiagonal = useCallback(
        ({ diagonal }: SelectDiagonalPayloadData) => {
            track('select_item', {
                category: 'diagonal',
                items: [
                    { item_id: diagonal.productId.toString(), item_name: getProductDescription(diagonal) } as GaItem,
                ],
            });
        },
        [track]
    );

    const trackSelectCustomSide = useCallback(
        ({ side }: SelectCustomSidePayloadData) => {
            track('select_item', {
                category: 'custom_side',
                items: [{ item_id: side.id.toString(), item_name: side.type } as GaItem],
            });
        },
        [track]
    );

    const trackSelectApplianceButton = useCallback(
        ({ appliance }: SelectApplianceButtonPayloadData) => {
            track('select_item', {
                category: 'appliance',
                items: [
                    {
                        item_id: appliance.productId.toString(),
                        item_name: getProductDescription(appliance),
                    } as GaItem,
                ],
            });
        },
        [track]
    );

    const trackAddAppliance = useCallback(
        ({ appliance }: AddAppliancePayloadData) => {
            track('add_to_cart', {
                category: 'appliance',
                items: [
                    { item_id: appliance.productId.toString(), item_name: getProductDescription(appliance) } as GaItem,
                ],
            });
        },
        [track]
    );

    const trackDeleteAppliance = useCallback(
        ({ appliance }: DeleteAppliancePayloadData) => {
            track('remove_from_cart', {
                category: 'appliance',
                items: [
                    {
                        item_id: appliance.productId.toString(),
                        item_name: getProductDescription(appliance),
                    } as GaItem,
                ],
            });
        },
        [track]
    );

    const trackSelectCutout = useCallback(
        ({ cutout }: SelectCutoutPayloadData) => {
            track('select_item', {
                category: 'cutout',
                items: [{ item_id: cutout.productId.toString(), item_name: getProductDescription(cutout) } as GaItem],
            });
        },
        [track]
    );

    const trackSelectTearout = useCallback(
        ({ tearout }: SelectTearoutPayloadData) => {
            track('select_item', {
                category: 'tearout',
                items: [{ item_id: tearout.productId.toString(), item_name: getProductDescription(tearout) } as GaItem],
            });
        },
        [track]
    );

    const trackSelectOptionalItem = useCallback(
        ({ optionalItem }: SelectOptionalItemPayloadData) => {
            track('select_item', {
                category: 'optional_item',
                items: [
                    {
                        item_id: optionalItem.productId.toString(),
                        item_name: getProductDescription(optionalItem),
                    } as GaItem,
                ],
            });
        },
        [track]
    );

    const buildCartPayload = useCallback(
        (pricedProducts: PricedProduct[], priceTotals: PriceTotalsReturn): ShoppingCart => {
            return {
                currency: 'USD',
                value: roundToNearest(priceTotals.priceWithDiscount, 2),
                items: pricedProducts.map((item) => {
                    return {
                        item_id: item.productId.toString(),
                        item_name: getProductDescription(item),
                        price: roundToNearest(item.unitPrice ?? 0, 2),
                        quantity: item.quantity,
                    } as GaItem;
                }),
            };
        },
        []
    );

    /**
     * track when the user clicks the Review Estimate button to go to the Summary page
     */
    const trackViewCart = useCallback(
        ({ pricedProducts, priceTotals }: ReviewEstimatePayloadData) => {
            const cartPayload = buildCartPayload(pricedProducts, priceTotals);
            track('view_cart', cartPayload);
        },
        [buildCartPayload, track]
    );

    const trackBeginCheckout = useCallback(
        ({ pricedProducts, priceTotals }: BeginCheckoutPayloadData) => {
            track('begin_checkout', buildCartPayload(pricedProducts, priceTotals));
        },
        [buildCartPayload, track]
    );

    const trackCustomerInfoSave = useCallback(
        ({ pricedProducts, priceTotals }: CustomerInfoSavePayloadData) => {
            track('add_shipping_info', buildCartPayload(pricedProducts, priceTotals));
        },
        [buildCartPayload, track]
    );

    const trackEstimateSaved = useCallback(
        ({ estimateNumber, pricedProducts, priceTotals }: EstimateSavedPayloadData) => {
            const cartPayload: SavedShoppingCart = {
                transaction_id: estimateNumber,
                ...buildCartPayload(pricedProducts, priceTotals),
            };
            track('purchase', cartPayload);
        },
        [buildCartPayload, track]
    );

    const trackVisualizer = useCallback(
        ({ action, payload }: VisualizerPayloadData) => {
            const event = `visualizer_${action.replace(/\s+/g, '_')}`;
            track(event, payload);
        },
        [track]
    );

    const trackCloneEstimate = useCallback(
        ({ status }: CloneEstimatePayload) => {
            track('clone_estimate', {
                status,
            });
        },
        [track]
    );

    return {
        track,
        page,
        reset,
        identify,
        trackSearchZipCode,
        trackSelectColor,
        trackSelectStore,
        trackSelectMaterial,
        trackAddShape,
        trackDeleteShape,
        trackSelectEdge,
        trackSelectRadius,
        trackSelectDiagonal,
        trackSelectCustomSide,
        trackSelectApplianceButton,
        trackAddAppliance,
        trackDeleteAppliance,
        trackSelectCutout,
        trackSelectTearout,
        trackSelectOptionalItem,
        trackViewCart,
        trackBeginCheckout,
        trackCustomerInfoSave,
        trackEstimateSaved,
        trackVisualizer,
        trackCloneEstimate,
    };
};
